// SCR-STAFF-800 (AP-CMP-01؛ US-CMP-001 تا 003، 010 تا 012، 014): قراردادهای همکاری با نسخه‌های تاریخ‌دار، قواعد سهم (نقش، دامنه، مبنا، روش) و تحمل
// تخفیف؛ ویرایش فقط پیش‌نویس، فعال‌سازی با جایگزینی نسخه قبلی (BR-CMP-004) و پایان نسخه فعال.
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type CmpAgreementStatus,
  type CmpAgreementSummaryView,
  type CmpAgreementVersionView,
  type CmpAgreementView,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  ConfirmDialog,
  DataTable,
  ErrorState,
  faDigits,
  FormDialog,
  JalaliDate,
  JalaliDatePickerRHF,
  ListPage,
  Money,
  StatusChip,
  zBusinessDate,
  type DataTableColumn,
} from '@phoenix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { AgreementDialog } from './AgreementDialog';
import {
  AGREEMENT_STATUSES,
  agreementTone,
  BEARING_KINDS,
  compensationKeys,
  fromBasisPoints,
  ledgerScopes,
  LIST_SEPARATOR,
  ProviderPicker,
} from './common';

export const SCR_STAFF_800 = 'SCR-STAFF-800';

export function AgreementsPage() {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('cmp.agreement.manage');
  const [providerId, setProviderId] = useState<string | null>(null);
  const [status, setStatus] = useState<CmpAgreementStatus | ''>('');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const agreements = useQuery({
    queryKey: [...compensationKeys.agreements(session?.tenantId), providerId, status, cursor],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/compensation/agreements', {
          params: {
            query: { providerId: providerId ?? undefined, status: status || undefined, cursor, limit: 50 },
          },
        }),
      ),
  });

  const columns: DataTableColumn<CmpAgreementSummaryView>[] = [
    { id: 'provider', headerKey: 'cmp:agreements.columns.provider', cell: (row) => row.providerName ?? '—' },
    { id: 'title', headerKey: 'cmp:agreements.columns.title', cell: (row) => row.title ?? '—' },
    {
      id: 'type',
      headerKey: 'cmp:agreements.columns.type',
      cell: (row) => t(`cmp:agreement_type.${row.type}`),
    },
    {
      id: 'version',
      headerKey: 'cmp:agreements.columns.version',
      cell: (row) => faDigits(Number(row.version)),
    },
    {
      id: 'status',
      headerKey: 'cmp:agreements.columns.status',
      cell: (row) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <StatusChip tone={agreementTone(row.status)} labelKey={`cmp:agreement_status.${row.status}`} />
          {row.hasDraft && row.status !== 'Draft' && (
            <Chip size="small" variant="outlined" label={t('cmp:agreements.draft_badge')} />
          )}
        </Stack>
      ),
    },
    {
      id: 'from',
      headerKey: 'cmp:agreements.columns.effective_from',
      cell: (row) => <JalaliDate value={row.effectiveFrom} />,
    },
    {
      id: 'to',
      headerKey: 'cmp:agreements.columns.effective_to',
      cell: (row) => (row.effectiveTo ? <JalaliDate value={row.effectiveTo} /> : '—'),
    },
    {
      id: 'rules',
      headerKey: 'cmp:agreements.columns.rules',
      cell: (row) => faDigits(Number(row.ruleCount)),
    },
    {
      id: 'actions',
      headerKey: 'cmp:agreements.columns.actions',
      cell: (row) => (
        <Button size="small" onClick={() => setOpenId(row.id)} data-testid={`agreement-open-${row.id}`}>
          {t('cmp:agreements.open')}
        </Button>
      ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_800}
      titleKey="cmp:agreements.title"
      subtitleKey="cmp:agreements.subtitle"
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreating(true)}
          disabled={!write.canWrite}
          data-testid="agreement-create"
        >
          {t('cmp:agreements.add')}
        </Button>
      }
      filters={
        <Stack spacing={1}>
          <WriteNotice write={write} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <ProviderPicker
              value={providerId}
              onChange={(id) => {
                setCursor(undefined);
                setProviderId(id);
              }}
              labelKey="cmp:agreements.filters.provider"
              testId="agreement-filter-provider"
            />
            <TextField
              select
              size="small"
              label={t('cmp:agreements.filters.status')}
              value={status}
              onChange={(event) => {
                setCursor(undefined);
                setStatus(event.target.value as CmpAgreementStatus | '');
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">{t('cmp:agreements.filters.all')}</MenuItem>
              {AGREEMENT_STATUSES.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`cmp:agreement_status.${value}`)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Stack>
      }
    >
      <DataTable
        captionKey="cmp:agreements.caption"
        columns={columns}
        rows={agreements.data?.items}
        getRowId={(row) => row.id}
        isLoading={agreements.isPending}
        error={agreements.error}
        onRetry={() => void agreements.refetch()}
        hasMore={Boolean(agreements.data?.nextCursor)}
        onLoadMore={() => setCursor(agreements.data?.nextCursor ?? undefined)}
        emptyTitleKey="cmp:agreements.empty"
      />
      {creating && (
        <AgreementDialog
          onClose={(saved) => {
            setCreating(false);
            if (saved) {
              setOpenId(saved.id);
            }
          }}
        />
      )}
      {openId && (
        <AgreementDetailDialog id={openId} canWrite={write.canWrite} onClose={() => setOpenId(null)} />
      )}
    </ListPage>
  );
}

/** جزئیات قرارداد با همه نسخه‌ها و اقدام‌های چرخه (SM-CompensationAgreement). */
function AgreementDetailDialog({
  id,
  canWrite,
  onClose,
}: {
  id: string;
  canWrite: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('cmp');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<CmpAgreementVersionView | null>(null);
  const [activating, setActivating] = useState<CmpAgreementVersionView | null>(null);
  const [dated, setDated] = useState<{ mode: 'version' | 'end'; version: CmpAgreementVersionView } | null>(
    null,
  );

  const agreement = useQuery({
    queryKey: compensationKeys.agreement(session?.tenantId, id),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/compensation/agreements/{id}', { params: { path: { id } } })),
  });

  const refresh = (view: CmpAgreementView) => {
    queryClient.setQueryData(compensationKeys.agreement(session?.tenantId, id), view);
    void queryClient.invalidateQueries({ queryKey: compensationKeys.agreements(session?.tenantId) });
    for (const key of ledgerScopes(session?.tenantId)) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
  };

  const activate = useMutation({
    mutationFn: (version: CmpAgreementVersionView) =>
      unwrap(
        tenantApi.POST('/api/v1/compensation/agreements/{id}/versions/{versionId}:activate', {
          params: { path: { id, versionId: version.id } },
        }),
      ),
    onSuccess: (view) => {
      refresh(view);
      setActivating(null);
    },
  });

  const view = agreement.data;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="agreement-detail-title"
      data-testid="agreement-detail"
    >
      <DialogTitle id="agreement-detail-title">
        {t('cmp:detail.title', { provider: view?.providerName ?? '' })}
      </DialogTitle>
      <DialogContent dividers>
        {agreement.error ? (
          <ErrorState error={agreement.error} onRetry={() => void agreement.refetch()} />
        ) : (
          <Stack spacing={2}>
            {view?.title && <Typography variant="subtitle1">{view.title}</Typography>}
            <Typography variant="caption" color="text.secondary">
              {t('cmp:detail.phase2_note')}
            </Typography>
            {view?.versions.map((version) => (
              <Stack key={version.id} spacing={1} data-testid={`agreement-version-${Number(version.number)}`}>
                <Divider />
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="subtitle2">
                    {t('cmp:detail.version_label', { number: faDigits(Number(version.number)) })}
                  </Typography>
                  <StatusChip
                    tone={agreementTone(version.status)}
                    labelKey={`cmp:agreement_status.${version.status}`}
                  />
                  <Typography variant="body2">
                    {t(`cmp:agreement_type.${version.type}`)} · <JalaliDate value={version.effectiveFrom} /> ←{' '}
                    {version.effectiveTo ? (
                      <JalaliDate value={version.effectiveTo} />
                    ) : (
                      t('cmp:detail.open_ended')
                    )}
                  </Typography>
                </Stack>
                <Stack component="ul" sx={{ m: 0, pl: 3 }} spacing={0.5}>
                  {version.rules.length === 0 && (
                    <Typography variant="body2">{t('cmp:form.rules_empty')}</Typography>
                  )}
                  {version.rules.map((rule) => (
                    <Typography key={rule.id} component="li" variant="body2">
                      {t(`cmp:role.${rule.role}`)} ·{' '}
                      {rule.scope === 'All'
                        ? t('cmp:scope.All')
                        : (rule.scopeName ?? t(`cmp:scope.${rule.scope}`))}{' '}
                      · {t(`cmp:share_basis.${rule.basis}`)} ·{' '}
                      {rule.method === 'Percent' ? (
                        t('cmp:detail.rate', { rate: faDigits(fromBasisPoints(rule.rateBasisPoints) ?? 0) })
                      ) : (
                        <Money amountMinor={Number(rule.amount?.amount ?? 0)} />
                      )}{' '}
                      {rule.method !== 'Percent' && `(${t(`cmp:method.${rule.method}`)})`}
                    </Typography>
                  ))}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {t('cmp:detail.bearing')}:{' '}
                  {BEARING_KINDS.map(
                    (kind) =>
                      `${t(`cmp:discount_kind.${kind}`)} ${t('cmp:detail.rate', { rate: faDigits(fromBasisPoints(version.discountBearing[kind]) ?? 0) })}`,
                  ).join(LIST_SEPARATOR)}
                </Typography>
                {(version.fixedCharges.length > 0 || version.minimumGuarantee) && (
                  <Typography variant="body2" color="text.secondary">
                    {t('cmp:detail.charges')}:{' '}
                    {version.fixedCharges
                      .map((charge) => t(`cmp:charge_kind.${charge.kind}`))
                      .join(LIST_SEPARATOR) || t('cmp:detail.no_charges')}
                  </Typography>
                )}
                {canWrite && (
                  <Stack direction="row" spacing={1}>
                    {version.status === 'Draft' && (
                      <>
                        <Button
                          size="small"
                          onClick={() => setEditing(version)}
                          data-testid={`agreement-edit-${Number(version.number)}`}
                        >
                          {t('cmp:detail.edit')}
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => setActivating(version)}
                          data-testid={`agreement-activate-${Number(version.number)}`}
                        >
                          {t('cmp:detail.activate')}
                        </Button>
                      </>
                    )}
                    {version.status === 'Active' &&
                      !view.versions.some((candidate) => candidate.status === 'Draft') && (
                        <Button
                          size="small"
                          onClick={() => setDated({ mode: 'version', version })}
                          data-testid="agreement-new-version"
                        >
                          {t('cmp:detail.new_version')}
                        </Button>
                      )}
                    {version.status === 'Active' && (
                      <Button
                        size="small"
                        color="warning"
                        onClick={() => setDated({ mode: 'end', version })}
                        data-testid="agreement-end"
                      >
                        {t('cmp:detail.end')}
                      </Button>
                    )}
                  </Stack>
                )}
              </Stack>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cmp:detail.close')}</Button>
      </DialogActions>
      {editing && view && (
        <AgreementDialog
          agreement={view}
          version={editing}
          onClose={(saved) => {
            setEditing(null);
            if (saved) {
              refresh(saved);
            }
          }}
        />
      )}
      <ConfirmDialog
        open={activating !== null}
        titleKey="cmp:detail.activate"
        messageKey="cmp:detail.activate_confirm"
        confirmKey="cmp:detail.activate"
        destructive={false}
        busy={activate.isPending}
        error={activate.error ?? undefined}
        onConfirm={() => activating && activate.mutate(activating)}
        onCancel={() => {
          activate.reset();
          setActivating(null);
        }}
      />
      {dated && view && (
        <DatedActionDialog
          agreementId={view.id}
          mode={dated.mode}
          version={dated.version}
          onClose={(saved) => {
            setDated(null);
            if (saved) {
              refresh(saved);
            }
          }}
        />
      )}
    </Dialog>
  );
}

const datedSchema = z.object({ date: zBusinessDate() });
type DatedForm = z.input<typeof datedSchema>;

/** نسخه تازه از تاریخ مؤثر (US-CMP-003) یا پایان نسخه فعال. */
function DatedActionDialog({
  agreementId,
  mode,
  version,
  onClose,
}: {
  agreementId: string;
  mode: 'version' | 'end';
  version: CmpAgreementVersionView;
  onClose: (saved?: CmpAgreementView) => void;
}) {
  const { tenantApi } = useTenantSession();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState, setError } = useForm<DatedForm>({
    resolver: zodResolver(datedSchema),
    defaultValues: { date: '' },
  });
  const save = useMutation({
    mutationFn: (form: DatedForm) =>
      unwrap(
        mode === 'version'
          ? tenantApi.POST('/api/v1/compensation/agreements/{id}/versions', {
              params: { path: { id: agreementId } },
              body: { effectiveFrom: form.date },
              headers: { 'Idempotency-Key': idempotency.current() },
            })
          : tenantApi.POST('/api/v1/compensation/agreements/{id}/versions/{versionId}:end', {
              params: { path: { id: agreementId, versionId: version.id } },
              body: { endDate: form.date },
            }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => {
      if (mode === 'version') {
        idempotency.settle(error ?? undefined);
      }
    },
    onSuccess: (saved) => onClose(saved),
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['date'],
        codeFields: { 'PHX-CMP-0203': 'date', 'PHX-CMP-0101': 'date', 'PHX-CMP-0204': 'date' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      maxWidth="xs"
      screenId="SCR-STAFF-800-DATED"
      titleKey={mode === 'version' ? 'cmp:detail.new_version_title' : 'cmp:detail.end_title'}
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={() => onClose()}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <JalaliDatePickerRHF
        control={control}
        name="date"
        labelKey={mode === 'version' ? 'cmp:form.effective_from' : 'cmp:detail.end_date'}
        required
      />
    </FormDialog>
  );
}
